import React, { useCallback, useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { ShieldCheck, ShieldX, Search, UserPlus, Lock, Mail, Calendar } from 'lucide-react';

import { Card } from './AdminPanelPrimitives';
import { ADMIN_PALETTE, EmptyState } from './adminDesignSystem';
import { formatEuropeanDateTime } from '../../utils/dateFormat';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const PromoteAdminModal = ({ onClose, onPromote, busy }) => {
  const [email, setEmail] = useState('');
  const [code, setCode] = useState('');
  const codeValid = /^\d{4}$/.test(code);
  const emailValid = email.includes('@') && email.includes('.');

  const submit = (event) => {
    event.preventDefault();
    if (!emailValid || !codeValid) return;
    onPromote({ email: email.trim().toLowerCase(), unlock_code: code.trim() });
  };

  return (
    <div
      data-testid="admin-promote-modal"
      role="dialog"
      aria-modal="true"
      style={{
        position: 'fixed',
        inset: 0,
        background: 'rgba(0,0,0,0.55)',
        backdropFilter: 'blur(6px)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 9999,
        padding: 16,
      }}
      onClick={() => !busy && onClose()}
    >
      <form
        onClick={(event) => event.stopPropagation()}
        onSubmit={submit}
        style={{
          maxWidth: 460,
          width: '100%',
          background: '#FFF',
          borderRadius: 22,
          padding: 22,
          boxShadow: '0 20px 60px rgba(0,0,0,0.25)',
          display: 'grid',
          gap: 14,
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <span style={{ width: 38, height: 38, borderRadius: 12, background: '#EAF7EE', display: 'inline-flex', alignItems: 'center', justifyContent: 'center' }}>
            <ShieldCheck size={20} color="#0F6B19" />
          </span>
          <div>
            <h3 style={{ fontFamily: "'Inria Serif', serif", fontSize: 19, margin: 0 }}>Promote new admin</h3>
            <p style={{ margin: '2px 0 0', fontSize: 12, color: '#6B7280' }}>
              Pick the user by email and set a 4-digit unlock code. Share the code with them — they'll need to type it once on their Switch-to page to unlock the Admin button.
            </p>
          </div>
        </div>

        <label htmlFor="admin-promote-email" style={{ display: 'grid', gap: 6 }}>
          <span style={{ fontSize: 11, fontWeight: 700, color: '#6B7280', textTransform: 'uppercase', letterSpacing: 0.5 }}>
            User email
          </span>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '10px 12px', borderRadius: 12, border: '1px solid #E5E7EB', background: '#F9FAFB' }}>
            <Mail size={14} color="#6B7280" />
            <input
              id="admin-promote-email"
              data-testid="admin-promote-modal-email"
              type="email"
              placeholder="founder@example.com"
              value={email}
              onChange={(event) => setEmail(event.target.value)}
              autoComplete="off"
              required
              style={{ flex: 1, border: 'none', outline: 'none', background: 'transparent', fontSize: 14, color: '#0F1115' }}
            />
          </div>
        </label>

        <label htmlFor="admin-promote-code" style={{ display: 'grid', gap: 6 }}>
          <span style={{ fontSize: 11, fontWeight: 700, color: '#6B7280', textTransform: 'uppercase', letterSpacing: 0.5 }}>
            4-digit unlock code
          </span>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '10px 12px', borderRadius: 12, border: '1px solid #E5E7EB', background: '#F9FAFB' }}>
            <Lock size={14} color="#6B7280" />
            <input
              id="admin-promote-code"
              data-testid="admin-promote-modal-code"
              inputMode="numeric"
              maxLength={4}
              placeholder="e.g. 4242"
              value={code}
              onChange={(event) => setCode(event.target.value.replace(/\D/g, '').slice(0, 4))}
              autoComplete="off"
              required
              style={{ flex: 1, border: 'none', outline: 'none', background: 'transparent', fontSize: 18, letterSpacing: 4, fontWeight: 700, color: '#0F1115' }}
            />
          </div>
          <span style={{ fontSize: 11, color: '#6B7280' }}>
            Remember this code — Allure doesn't store it in plain text. Tell the new admin verbally or send a separate channel.
          </span>
        </label>

        <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
          <button
            type="button"
            data-testid="admin-promote-modal-cancel"
            onClick={onClose}
            disabled={busy}
            style={{ padding: '8px 14px', borderRadius: 9999, border: '1px solid #E5E7EB', background: '#FFF', color: '#333', fontWeight: 700, fontSize: 13, cursor: busy ? 'not-allowed' : 'pointer' }}
          >
            Cancel
          </button>
          <button
            type="submit"
            data-testid="admin-promote-modal-confirm"
            disabled={busy || !codeValid || !emailValid}
            style={{
              padding: '8px 16px',
              borderRadius: 9999,
              border: 'none',
              background: '#0F6B19',
              color: '#FFF',
              fontWeight: 700,
              fontSize: 13,
              cursor: busy || !codeValid || !emailValid ? 'not-allowed' : 'pointer',
              opacity: busy || !codeValid || !emailValid ? 0.65 : 1,
              display: 'inline-flex',
              alignItems: 'center',
              gap: 6,
            }}
          >
            <ShieldCheck size={14} />
            {busy ? 'Promoting…' : 'Promote'}
          </button>
        </div>
      </form>
    </div>
  );
};

export const AdminAdminsPanel = ({ headers, currentUser }) => {
  const [admins, setAdmins] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('');
  const [modalOpen, setModalOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const isMainAdmin = Boolean(currentUser?.is_main_admin);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/admin/admins/list`, { headers });
      setAdmins(res.data || []);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to load admins');
    } finally {
      setLoading(false);
    }
  }, [headers]);

  useEffect(() => { load(); }, [load]);

  const handlePromote = async ({ email, unlock_code }) => {
    setBusy(true);
    try {
      const res = await axios.post(`${API}/admin/admins/promote`, { email, unlock_code }, { headers });
      toast.success(`${res.data?.user?.email || email} promoted to admin`);
      setModalOpen(false);
      await load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to promote user');
    } finally {
      setBusy(false);
    }
  };

  const handleRevoke = async (admin) => {
    if (!window.confirm(`Revoke admin access from ${admin.email}? Their Admin button will disappear.`)) return;
    setBusy(true);
    try {
      await axios.post(`${API}/admin/admins/${admin.id}/revoke`, {}, { headers });
      toast.success('Admin access revoked');
      await load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to revoke admin');
    } finally {
      setBusy(false);
    }
  };

  const visible = useMemo(() => {
    const q = filter.trim().toLowerCase();
    if (!q) return admins;
    return admins.filter((a) => [a.email, a.name].filter(Boolean).some((v) => String(v).toLowerCase().includes(q)));
  }, [admins, filter]);

  return (
    <div data-testid="admin-admins-panel" style={{ display: 'grid', gap: 12 }}>
      <Card>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <ShieldCheck size={22} color={ADMIN_PALETTE.brandRed} />
          <div style={{ flex: 1 }}>
            <h2 style={{ fontFamily: "'Inria Serif', serif", fontSize: 22, margin: 0 }}>Promoted admins</h2>
            <p style={{ margin: '2px 0 0', fontSize: 13, color: '#6B7280' }}>
              These users gained admin access via a 4-digit unlock code. Main admins can promote/revoke at any time.
            </p>
          </div>
          {isMainAdmin ? (
            <button
              type="button"
              data-testid="admin-admins-promote-button"
              onClick={() => setModalOpen(true)}
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: 6,
                padding: '10px 14px',
                borderRadius: 12,
                border: 'none',
                background: '#0F6B19',
                color: '#FFF',
                fontWeight: 700,
                fontSize: 13,
                cursor: 'pointer',
              }}
            >
              <UserPlus size={14} />
              Promote new admin
            </button>
          ) : null}
        </div>
      </Card>

      <Card>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 10 }}>
          <div style={{ flex: 1, display: 'flex', alignItems: 'center', gap: 8, padding: '10px 12px', borderRadius: 12, border: '1px solid #E5E7EB', background: '#F6F7F9' }}>
            <Search size={16} color="#6B7280" />
            <input
              type="text"
              value={filter}
              onChange={(e) => setFilter(e.target.value)}
              placeholder="Filter by email or name"
              data-testid="admin-admins-filter-input"
              style={{ flex: 1, border: 'none', outline: 'none', background: 'transparent', fontSize: 14 }}
            />
          </div>
          <span style={{ fontSize: 12, color: '#6B7280', fontWeight: 600 }}>
            {visible.length} of {admins.length}
          </span>
        </div>

        {loading ? (
          <p style={{ textAlign: 'center', color: '#999', padding: 20 }}>Loading…</p>
        ) : admins.length === 0 ? (
          <EmptyState
            icon={ShieldCheck}
            tone="active"
            title="No promoted admins yet"
            body={
              isMainAdmin
                ? 'Tap "Promote new admin" to grant admin access to a teammate. They\'ll need the 4-digit code you set to unlock it.'
                : 'Only main admins can promote new operators.'
            }
            actionLabel={isMainAdmin ? 'Promote new admin' : undefined}
            onAction={isMainAdmin ? () => setModalOpen(true) : undefined}
            dataTestId="admin-admins-empty"
          />
        ) : visible.length === 0 ? (
          <p data-testid="admin-admins-no-match" style={{ textAlign: 'center', color: '#999', padding: 20 }}>No admins match this filter.</p>
        ) : (
          <div style={{ display: 'grid', gap: 8 }}>
            {visible.map((admin) => (
              <div
                key={admin.id}
                data-testid={`admin-admins-row-${admin.id}`}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 12,
                  padding: '12px 14px',
                  borderRadius: 14,
                  border: '1px solid #E5E7EB',
                  background: '#FFFFFF',
                }}
              >
                <span style={{ width: 38, height: 38, borderRadius: '50%', background: '#F3EEFF', color: '#3F1F8C', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', fontWeight: 800 }}>
                  {(admin.name || admin.email || '?').trim().charAt(0).toUpperCase()}
                </span>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <p style={{ margin: 0, fontSize: 14, fontWeight: 700, color: '#0F1115' }}>{admin.name || admin.email}</p>
                  <p style={{ margin: '2px 0 0', fontSize: 12, color: '#6B7280' }}>{admin.email}</p>
                  <p style={{ margin: '2px 0 0', fontSize: 11, color: '#9CA3AF', display: 'inline-flex', alignItems: 'center', gap: 4 }}>
                    <Calendar size={11} /> Promoted {admin.admin_promoted_at ? formatEuropeanDateTime(admin.admin_promoted_at) : '—'}
                    {' · '}
                    {admin.is_admin_unlocked ? (
                      <span style={{ color: '#0F6B19', fontWeight: 700 }}>Unlocked ✓</span>
                    ) : (
                      <span style={{ color: '#A36500', fontWeight: 700 }}>Awaiting unlock</span>
                    )}
                  </p>
                </div>
                {isMainAdmin ? (
                  <button
                    type="button"
                    data-testid={`admin-admins-revoke-${admin.id}`}
                    onClick={() => handleRevoke(admin)}
                    disabled={busy}
                    style={{
                      display: 'inline-flex',
                      alignItems: 'center',
                      gap: 6,
                      padding: '8px 12px',
                      borderRadius: 10,
                      border: '1px solid #FBD3D3',
                      background: '#FFF1F1',
                      color: '#B91C1C',
                      fontWeight: 700,
                      fontSize: 12,
                      cursor: busy ? 'not-allowed' : 'pointer',
                    }}
                  >
                    <ShieldX size={13} />
                    Revoke
                  </button>
                ) : null}
              </div>
            ))}
          </div>
        )}
      </Card>

      {modalOpen ? (
        <PromoteAdminModal
          onClose={() => setModalOpen(false)}
          onPromote={handlePromote}
          busy={busy}
        />
      ) : null}
    </div>
  );
};

export default AdminAdminsPanel;
